// src/engine/projectFinancialsEngine.ts

import {
  Project,
  PaymentReceived,
  Expense,
  WageEntry,
  Partner,
  PartnerDisbursement,
  ScopeItem,
  ProjectTransfer,
} from "../data/mockData";

/* ======================================================
   TYPES
====================================================== */

export type PaymentMethod = "Cash" | "Bank";

export type EngineInputs = {
  projects: Project[];
  partners: Partner[];
  scopeItems: ScopeItem[];
  paymentsReceived: PaymentReceived[];
  expenses: Expense[];
  wageEntries: WageEntry[];
  partnerDisbursements: PartnerDisbursement[];
  projectTransfers: ProjectTransfer[];
};

export type PartnerLedgerState = {
  partnerId: string;
  partnerName: string;

  carryForwardPayable: number;
  recoveredFromFutureProfits: number;

  // if partner took more advance than their profit share, this is owed back
  advanceCarryForwardOwed: number;
};

export type PartnerProjectSnapshot = {
  partnerId: string;
  partnerName: string;

  paidByPartner: {
    expenses: number;
    subcontracts: number;
    wages: number; // schema limitation right now
    total: number;
  };

  // advances taken on this project (draws), NOT a cost
  advancesTaken: number;

  reimbursableClaim: number;
  reimbursedThisProject: number;
  unpaidCarryForwardAdded: number;
};

export type MethodBreakdown = {
  cashIn: number;
  bankIn: number;

  // True costs (by method) — paid-to-date only
  cashOut: number;
  bankOut: number;

  transfersInCash: number;
  transfersInBank: number;
  transfersOutCash: number;
  transfersOutBank: number;

  // receipts - true costs
  cashSurplus: number;
  bankSurplus: number;

  // advances tracked separately (NOT a cost)
  advancesCash: number;
  advancesBank: number;

  // what remains after advances (helps UI for settlement by method)
  availableToSettleCash: number;
  availableToSettleBank: number;
};

export type ProjectTotals = {
  totalEstimate: number;
  totalReceived: number;

  // wages totals
  wagesTotal: number;        // paid + unpaid (visibility)
  wagesPaidTotal: number;    // paid-only (true-costs)
  wagesUnpaidTotal: number;  // unpaid-only (liability)

  subcontractPaidTotal: number;
  expensesOtherTotal: number;

  // tracked separately (NOT a cost)
  advancesTotal: number;

  // TRUE COSTS (paid-to-date; excludes advances; excludes unpaid wages)
  totalOut: number;

  // receipts - true costs (excludes advances)
  cashSurplus: number;

  // estimate - true costs (excludes advances)
  economicProfit: number;

  // cash/bank tracking
  method: MethodBreakdown;
};

export type ProjectPartnerSettlement = {
  partnerId: string;
  partnerName: string;

  carryForwardBefore: number;
  recoveredThisProject: number;
  carryForwardAfter: number;

  profitShareThisProject: number;

  advancesTakenThisProject: number;

  profitPayoutAfterAdvances: number;

  advanceCarryForwardOwedAfterThisProject: number;
};

export type ProjectFinancials = {
  projectId: string;
  projectName: string;

  totals: ProjectTotals;

  partnerSnapshots: PartnerProjectSnapshot[];
  partnerSettlements: ProjectPartnerSettlement[];

  profitRemainingAfterAllocations: number;
};

export type EngineResult = {
  byProjectId: Record<string, ProjectFinancials>;
  projectOrder: string[];
  partnerLedgerFinal: PartnerLedgerState[];
};

/* ======================================================
   ENGINE
====================================================== */

export function computeProjectFinancialsEngine(inputs: EngineInputs): EngineResult {
  const {
    projects,
    partners,
    scopeItems,
    paymentsReceived,
    expenses,
    wageEntries,
    partnerDisbursements,
    projectTransfers,
  } = inputs;

  const sumAmount = <T extends { amount: number }>(arr: T[]) =>
    arr.reduce((s, x) => s + (Number(x.amount) || 0), 0);

  const isCash = (m: string) => (m || "").toLowerCase() === "cash";
  const isBank = (m: string) => (m || "").toLowerCase() === "bank";

  const projectOrder = projects.map((p) => p.id);

  /* ======================================================
     PARTNER LEDGER
  ====================================================== */

  const ledger: Record<string, PartnerLedgerState> = {};
  partners.forEach((p) => {
    ledger[p.id] = {
      partnerId: p.id,
      partnerName: p.name,
      carryForwardPayable: 0,
      recoveredFromFutureProfits: 0,
      advanceCarryForwardOwed: 0,
    };
  });

  const byProjectId: Record<string, ProjectFinancials> = {};

  /* ======================================================
     PROCESS PROJECTS SEQUENTIALLY
  ====================================================== */

  for (const pid of projectOrder) {
    const project = projects.find((p) => p.id === pid);
    if (!project) continue;

    /* ---------- ESTIMATE ---------- */
    const totalEstimate = scopeItems
      .filter((s) => s.projectId === pid)
      .reduce((s, x) => s + (Number(x.estimate) || 0), 0);

    /* ---------- RECEIPTS ---------- */
    const paymentsP = paymentsReceived.filter((p) => p.projectId === pid);
    const cashIn = sumAmount(paymentsP.filter((p) => isCash(p.paymentMethod)));
    const bankIn = sumAmount(paymentsP.filter((p) => isBank(p.paymentMethod)));
    const totalReceived = cashIn + bankIn;

    /* ---------- EXPENSES (TRUE COSTS) ---------- */
    const expP = expenses.filter((e) => e.projectId === pid);

    // IMPORTANT: Expense has `paymentMethod` (NOT `method`)
    const expensesOtherCash = sumAmount(
      expP.filter((e) => e.expenseType === "Expense" && isCash(e.paymentMethod))
    );
    const expensesOtherBank = sumAmount(
      expP.filter((e) => e.expenseType === "Expense" && isBank(e.paymentMethod))
    );

    const subcontractCash = sumAmount(
      expP.filter(
        (e) => e.expenseType === "Sub-Contract Payment" && isCash(e.paymentMethod)
      )
    );
    const subcontractBank = sumAmount(
      expP.filter(
        (e) => e.expenseType === "Sub-Contract Payment" && isBank(e.paymentMethod)
      )
    );

    const expensesOtherTotal = expensesOtherCash + expensesOtherBank;
    const subcontractPaidTotal = subcontractCash + subcontractBank;

    /* ---------- WAGES (PAID vs UNPAID + METHOD) ---------- */
    const wagesP = wageEntries.filter((w) => w.projectId === pid);

    // Total wages (paid + unpaid) — for visibility
    const wagesTotal = wagesP.reduce((s, w) => s + (Number(w.totalWages) || 0), 0);

    // Paid-only wages drive true costs and cash/bank pools
    let wagesPaidTotal = 0;
    let wagesUnpaidTotal = 0;

    let wagesPaidCash = 0;
    let wagesPaidBank = 0;

    for (const w of wagesP) {
      const amt = Number(w.totalWages) || 0;
      if (!amt) continue;

      const paid = !!(w as any).paid;

      if (!paid) {
        wagesUnpaidTotal += amt;
        continue;
      }

      wagesPaidTotal += amt;

      const pm = String((w as any).paidMethod || "").toLowerCase();
      if (pm === "bank") wagesPaidBank += amt;
      else wagesPaidCash += amt; // default cash when missing/unknown
    }

    /* ---------- TRUE COSTS (exclude advances; exclude unpaid wages) ---------- */
    const totalOut = wagesPaidTotal + expensesOtherTotal + subcontractPaidTotal;

    /* ---------- ADVANCES (FROM PARTNER DISBURSEMENTS ONLY) ---------- */
    // Option B: Advances are tracked ONLY as partner disbursements
    const pdP = partnerDisbursements.filter((d) => d.projectId === pid);

    const advancesCash = sumAmount(
      pdP.filter((d) => (d.kind || "Advance") === "Advance" && isCash(d.method))
    );
    const advancesBank = sumAmount(
      pdP.filter((d) => (d.kind || "Advance") === "Advance" && isBank(d.method))
    );
    const advancesTotal = advancesCash + advancesBank;

    /* ---------- PROFITS ---------- */
    // Paid-to-date surplus (true costs paid-to-date)
    const cashSurplus = totalReceived - totalOut;

    // Economic profit: estimate minus paid-to-date costs (useful but not final closeout)
    const economicProfit = totalEstimate - totalOut;

    /* ---------- METHOD BREAKDOWN ---------- */
    // IMPORTANT: cashOut/bankOut are PAID-to-date only (include paid wages by paidMethod)
    const cashOut = expensesOtherCash + subcontractCash + wagesPaidCash;
    const bankOut = expensesOtherBank + subcontractBank + wagesPaidBank;

    const transfersInCash = sumAmount(projectTransfers.filter(
      (t) => t.destinationProjectId === pid && isCash(t.paymentMethod)
    ));
    const transfersInBank = sumAmount(projectTransfers.filter(
      (t) => t.destinationProjectId === pid && isBank(t.paymentMethod)
    ));
    const transfersOutCash = sumAmount(projectTransfers.filter(
      (t) => t.sourceProjectId === pid && isCash(t.paymentMethod)
    ));
    const transfersOutBank = sumAmount(projectTransfers.filter(
      (t) => t.sourceProjectId === pid && isBank(t.paymentMethod)
    ));

    // Funding pool only: transfers move available funds but never revenue, costs, or profit.
    const cashSurplusByMethod = cashIn - cashOut + transfersInCash - transfersOutCash;
    const bankSurplusByMethod = bankIn - bankOut + transfersInBank - transfersOutBank;

    // "available to settle" = pool surplus minus advances already taken from that pool
    const availableToSettleCash = cashSurplusByMethod - advancesCash;
    const availableToSettleBank = bankSurplusByMethod - advancesBank;

    /* ======================================================
       PARTNER SNAPSHOTS (OUT-OF-POCKET + ADVANCES)
    ====================================================== */

    const partnerSnapshots: PartnerProjectSnapshot[] = partners.map((partner) => {
      const paidExpenses = sumAmount(
        expP.filter((e) => e.expenseType === "Expense" && e.whoPaid === partner.name)
      );

      const paidSubs = sumAmount(
        expP.filter(
          (e) =>
            e.expenseType === "Sub-Contract Payment" && e.whoPaid === partner.name
        )
      );

      const paidWages = 0; // schema limitation for now

      const advancesTaken = sumAmount(
        pdP.filter(
          (d) => (d.kind || "Advance") === "Advance" && d.partnerId === partner.id
        )
      );

      const reimbursableClaim = paidExpenses + paidSubs + paidWages;

      return {
        partnerId: partner.id,
        partnerName: partner.name,
        paidByPartner: {
          expenses: paidExpenses,
          subcontracts: paidSubs,
          wages: paidWages,
          total: paidExpenses + paidSubs + paidWages,
        },
        advancesTaken,
        reimbursableClaim,
        reimbursedThisProject: 0,
        unpaidCarryForwardAdded: 0,
      };
    });

    /* ======================================================
       REIMBURSEMENT FIRST (FROM RECEIPTS)
       (kept exactly as you had it)
    ====================================================== */

    let availableForReimbursement = totalReceived;

    for (const snap of partnerSnapshots) {
      if (snap.reimbursableClaim <= 0) continue;

      const reimbursed = Math.min(snap.reimbursableClaim, availableForReimbursement);
      snap.reimbursedThisProject = reimbursed;
      availableForReimbursement -= reimbursed;

      const unpaid = snap.reimbursableClaim - reimbursed;
      if (unpaid > 0) {
        ledger[snap.partnerId].carryForwardPayable += unpaid;
        snap.unpaidCarryForwardAdded = unpaid;
      }
    }

    /* ======================================================
       APPLY PROFIT TO PRIOR CARRY-FORWARD
       (kept exactly as you had it)
    ====================================================== */

    let profitToAllocate = Math.max(cashSurplus, 0);

    const partnerSettlements: ProjectPartnerSettlement[] = partners.map((p) => {
      const snap = partnerSnapshots.find((s) => s.partnerId === p.id);
      const advancesTakenThisProject = snap?.advancesTaken || 0;

      return {
        partnerId: p.id,
        partnerName: p.name,
        carryForwardBefore: ledger[p.id].carryForwardPayable,
        recoveredThisProject: 0,
        carryForwardAfter: ledger[p.id].carryForwardPayable,
        profitShareThisProject: 0,
        advancesTakenThisProject,
        profitPayoutAfterAdvances: 0,
        advanceCarryForwardOwedAfterThisProject: ledger[p.id].advanceCarryForwardOwed,
      };
    });

    if (profitToAllocate > 0) {
      // Phase 1: recover carry-forward payable
      for (const p of partners) {
        if (profitToAllocate <= 0) break;

        const owed = ledger[p.id].carryForwardPayable;
        if (owed <= 0) continue;

        const recovered = Math.min(owed, profitToAllocate);
        ledger[p.id].carryForwardPayable -= recovered;
        ledger[p.id].recoveredFromFutureProfits += recovered;
        profitToAllocate -= recovered;

        const row = partnerSettlements.find((x) => x.partnerId === p.id);
        if (row) {
          row.recoveredThisProject = recovered;
          row.carryForwardAfter = ledger[p.id].carryForwardPayable;
        }
      }

      // Phase 2: only if NO carry-forward left, share remaining profit equally
      const anyCarryLeft = partners.some((p) => ledger[p.id].carryForwardPayable > 0);

      if (!anyCarryLeft && profitToAllocate > 0) {
        const share = profitToAllocate / partners.length;
        partnerSettlements.forEach((ps) => (ps.profitShareThisProject = share));
        profitToAllocate = 0;
      }
    }

    /* ======================================================
       APPLY ADVANCES TO PROFIT PAYOUT
       (kept exactly as you had it)
    ====================================================== */

    for (const row of partnerSettlements) {
      const priorAdvanceOwed = ledger[row.partnerId].advanceCarryForwardOwed;
      const totalAdvanceDeductions = priorAdvanceOwed + row.advancesTakenThisProject;

      const net = row.profitShareThisProject - totalAdvanceDeductions;

      if (net >= 0) {
        row.profitPayoutAfterAdvances = net;
        ledger[row.partnerId].advanceCarryForwardOwed = 0;
        row.advanceCarryForwardOwedAfterThisProject = 0;
      } else {
        row.profitPayoutAfterAdvances = 0;
        const owedForward = Math.abs(net);
        ledger[row.partnerId].advanceCarryForwardOwed = owedForward;
        row.advanceCarryForwardOwedAfterThisProject = owedForward;
      }
    }

    byProjectId[pid] = {
      projectId: pid,
      projectName: project.name,
      totals: {
        totalEstimate,
        totalReceived,

        wagesTotal,
        wagesPaidTotal,
        wagesUnpaidTotal,

        subcontractPaidTotal,
        expensesOtherTotal,

        advancesTotal,

        totalOut,

        cashSurplus,
        economicProfit,

        method: {
          cashIn,
          bankIn,
          cashOut,
          bankOut,
          transfersInCash,
          transfersInBank,
          transfersOutCash,
          transfersOutBank,
          cashSurplus: cashSurplusByMethod,
          bankSurplus: bankSurplusByMethod,
          advancesCash,
          advancesBank,
          availableToSettleCash,
          availableToSettleBank,
        },
      },
      partnerSnapshots,
      partnerSettlements,
      profitRemainingAfterAllocations: profitToAllocate,
    };
  }

  return {
    byProjectId,
    projectOrder,
    partnerLedgerFinal: partners.map((p) => ledger[p.id]),
  };
}
